import type { MediaTarget } from '@/api/core/property-media';
import {
  createMediaSelection,
  readMediaEditContext,
  readOwnerMediaSelectionContext,
  replaceMediaItem,
  replaceMediaSelection,
  selectionCommand,
  type CreateMediaSelection,
  type EditSelection,
  type ItemCommand,
  type MediaEditContext,
  type MediaRetirementCommand,
  type OwnerMediaSelectionContext,
  type SelectionCommand,
} from '@/api/core/property-media-edit';
import { uploadManagedImage, type UploadManagedImage } from '@/api/core/property-media-write';
import { isApiError } from '@/api/http/errors';
import { safeImageUri } from '@/lib/safe-image-uri';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@mgrvtechgroup/admin-ds';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Choice } from '../presentation/choice';
import type { PropertyRuntime } from '../property-queries';
import { SELECTION_ROLE_LABEL } from './media-presentation';
import {
  canManageMedia,
  useMediaScope,
  useMediaWriteOwner,
  verifyMediaActor,
} from './media-write-form';
export interface EditPending {
  edit: true;
  collectionId: string;
  itemId: string;
  actorId?: string;
  stage:
    | 'read'
    | 'upload'
    | 'read-upload'
    | 'ready'
    | 'item'
    | 'create-selection'
    | 'selection'
    | 'verify'
    | 'unapplied'
    | 'acknowledge';
  retirement?: {
    target: MediaTarget;
    body: MediaRetirementCommand;
    accepted: boolean;
  };
  context?: MediaEditContext;
  upload?: UploadManagedImage;
  newItem?: string;
  itemBody?: ItemCommand;
  selectionBody?: SelectionCommand & { expected_owner_version: number };
  assignment?: string;
  createBody?: CreateMediaSelection;
  conflict?: boolean;
}
function scopeMatches(c: MediaEditContext, property: string, target: MediaTarget) {
  return (
    c.owner.property_id === property &&
    (target.kind === 'PROPERTY'
      ? c.owner.scope_kind === 'PROPERTY'
      : target.kind === 'ROOM_TYPE'
        ? c.owner.scope_kind === 'ROOM_TYPE' && c.owner.room_type_id === target.id
        : target.kind === 'SLOT' && c.owner.scope_kind === 'UNIT' && c.owner.unit_id === target.id)
  );
}
function required<T>(value: T | undefined | null): T {
  if (value == null) throw new Error('Incomplete media command');
  return value;
}
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
function SelectionPhoto({
  item,
  registeredOrder,
  collectionId,
  itemId,
}: {
  item: OwnerMediaSelectionContext['items'][number] | undefined;
  /** 현재 대상의 등록 사진 목록(서버 순서)에서 몇 번째인지. 목록에 없으면 null. */
  registeredOrder: number | null;
  collectionId: string;
  itemId: string;
}) {
  const [failed, setFailed] = useState(false);
  // Owner context has no authenticated managed-content path. Never synthesize a public URL.
  const uri =
    item?.codec_version === 2 && item.availability === 'CURRENT'
      ? (safeImageUri(item.preview_uri) ?? safeImageUri(item.primary_uri))
      : null;
  return (
    <div className="grid w-full min-w-0 gap-1">
      <p className="font-medium break-words">
        {item?.title?.trim() ? item.title : '제목 없는 사진'}
      </p>
      {!item?.title?.trim() && (
        <p className="caption">
          {registeredOrder === null
            ? '현재 등록 사진 목록에서 찾지 못한 사진'
            : `등록 사진 ${String(registeredOrder)}번째`}
        </p>
      )}
      {uri && !failed ? (
        <img
          className="h-16 w-24 rounded border object-contain"
          src={uri}
          alt="선택 사진 미리보기"
          onError={() => {
            setFailed(true);
          }}
        />
      ) : (
        <p className="caption text-muted-foreground">미리보기 제공 안 됨</p>
      )}
      <details className="caption text-muted-foreground min-w-0">
        <summary>사진 식별 정보</summary>
        <p className="break-all">
          {collectionId} / {itemId}
        </p>
      </details>
    </div>
  );
}
function registeredPhoto(
  context: OwnerMediaSelectionContext | null,
  collectionId: string,
  itemId: string,
) {
  const index =
    context?.items.findIndex(
      (item) => item.collection_id === collectionId && item.item_id === itemId,
    ) ?? -1;
  return { item: context?.items[index], registeredOrder: index < 0 ? null : index + 1 };
}
export function MediaEditForm({
  runtime,
  generation,
  propertyId,
  target,
  collectionId,
  itemId,
  onSaved,
  onAction,
  originalPreview,
  initialSelection,
  isCurrent = () => true,
}: {
  runtime: PropertyRuntime;
  generation: number;
  propertyId: string;
  target: MediaTarget;
  collectionId: string;
  itemId: string;
  onSaved: (receipt: string) => void;
  onAction?: () => void;
  originalPreview?: ReactNode;
  initialSelection?: {
    rootAbsent: boolean;
    category: string | null;
    language: string;
    selectionSupported?: boolean;
  };
  isCurrent?: () => boolean;
}) {
  const fieldId = useId();
  // A display has no DTO entry ID; repeated references/roles are valid entries.
  // Keep local IDs on the parsed objects and carry them through order-only copies.
  const [rowIds] = useState(() => new WeakMap<EditSelection['displays'][number], string>());
  const [rowSequence] = useState(() => ({ next: 0 }));
  const rowId = (display: EditSelection['displays'][number]) => {
    let id = rowIds.get(display);
    if (!id) {
      id = `${fieldId}-${String(rowSequence.next++)}`;
      rowIds.set(display, id);
    }
    return id;
  };
  const selectionRows = useRef(new Map<string, HTMLLIElement>());
  const movedFocus = useRef<{ id: string; step: -1 | 1 } | null>(null);
  const owner = useMediaWriteOwner();
  const { mounted, track } = useMediaScope(owner);
  const [contextEpoch, setContextEpoch] = useState(owner.epoch());
  const [context, setContext] = useState<MediaEditContext | null>(null);
  const [ownerContext, setOwnerContext] = useState<OwnerMediaSelectionContext | null>(null);
  const [selection, setSelection] = useState<EditSelection | null>(null);
  useEffect(() => {
    if (!movedFocus.current) return;
    const row = selectionRows.current.get(movedFocus.current.id);
    (
      row?.querySelector<HTMLButtonElement>(
        `button[data-step="${String(movedFocus.current.step)}"]:not(:disabled)`,
      ) ?? row?.querySelector<HTMLButtonElement>('button:not(:disabled)')
    )?.focus({ preventScroll: true });
    movedFocus.current = null;
  }, [selection]);
  const [url, setUrl] = useState('');
  const [newRole, setNewRole] = useState<'PROPERTY_CARD' | 'PROPERTY_HERO'>('PROPERTY_HERO');
  const [newVisible, setNewVisible] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState(false);
  const focus = useRef<HTMLParagraphElement>(null);
  const editHeading = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (context && !busy && !error) {
      editHeading.current?.focus({ preventScroll: true });
      if (typeof editHeading.current?.scrollIntoView === 'function')
        editHeading.current.scrollIntoView({ block: 'start' });
    }
  }, [context, busy, error]);
  useEffect(() => {
    if (error && !busy) focus.current?.focus();
  }, [error, busy, message]);
  const pending = owner.editAttempt;
  const mine =
    pending?.collectionId === collectionId && pending.itemId === itemId && !pending.retirement
      ? pending
      : null;
  const previewFile = mine?.upload?.file ?? file;
  const validFile = (candidate: File | null | undefined) =>
    !!candidate &&
    ['image/jpeg', 'image/png', 'image/webp'].includes(candidate.type) &&
    candidate.size > 0 &&
    candidate.size <= 10_000_000;
  const [localPreview, setLocalPreview] = useState<{ file: File; uri: string } | null>(null);
  useEffect(() => {
    if (!previewFile || !validFile(previewFile)) return;
    const uri = URL.createObjectURL(previewFile);
    setLocalPreview({ file: previewFile, uri });
    return () => {
      URL.revokeObjectURL(uri);
    };
  }, [previewFile]);

  const blocked =
    busy || !!owner.progress || !!owner.attempt || !!owner.createAttempt || (!!pending && !mine);
  const install = (c: MediaEditContext) => {
    setContextEpoch(owner.epoch());
    setContext(c);
    setSelection(c.affected_selections[0] ?? null);
    setUrl(c.primary_uri ?? '');
  };
  const run = async (p: EditPending) => {
    if (
      busy ||
      !canManageMedia(runtime, generation) ||
      !isCurrent() ||
      (p.stage !== 'read' && !pending && contextEpoch !== owner.epoch())
    )
      return;
    const lease = owner.claim(p);
    if (!lease) return;
    const replaying = owner.replayableEdit(lease);
    track(lease);
    const current = () =>
      mounted() && isCurrent() && owner.current(lease) && canManageMedia(runtime, generation);
    const requireActor = async () => {
      const actor = await verifyMediaActor(runtime, generation);
      if (!current()) throw new Error('obsolete');
      if (!actor) {
        owner.clear(lease);
        throw new Error('actor');
      }
      if (!owner.bind(lease, actor)) throw new Error('actor');
    };
    const read = async (item = itemId) => {
      const c = await readMediaEditContext(runtime.client, collectionId, item);
      if (!current() || !scopeMatches(c, propertyId, target)) throw new Error('scope');
      return c;
    };
    onAction?.();
    setBusy(true);
    setMessage('현재 선택 확인 중…');
    setError(false);
    try {
      await requireActor();
      if (p.stage === 'read') {
        const c = await read();
        if (!current()) return;
        install(c);
        if (initialSelection?.selectionSupported !== false || c.affected_selections.length > 0) {
          const full = await readOwnerMediaSelectionContext(runtime.client, c.owner);
          if (!current()) return;
          setOwnerContext(full);
          setSelection(full.current_selection);
        }
        owner.clear(lease);
        setMessage('');
        return;
      }
      if (p.stage === 'upload') {
        lease.sent = true;
        const receipt = await uploadManagedImage(runtime.client, required(p.upload));
        if (!current()) return;
        if (
          receipt.httpStatus !== 201 ||
          !receipt.itemId ||
          receipt.itemId === itemId ||
          receipt.collectionRevision !== required(p.upload).expectedCollectionRevision + 1
        )
          throw new Error('receipt');
        p.newItem = receipt.itemId;
        p.stage = 'read-upload';
      }
      if (p.stage === 'read-upload' || p.stage === 'acknowledge') {
        owner.checking(lease);
        const uploaded = await read(p.newItem);
        if (!current()) return;
        if (
          uploaded.codec_version !== 3 ||
          uploaded.expected_collection_revision < required(p.upload).expectedCollectionRevision + 1
        )
          throw new Error('upload effect');
        const c = await read();
        if (!current()) return;
        install(c);
        p.context = c;
        if (p.stage === 'acknowledge') {
          setContext(null);
          setSelection(null);
          setFile(null);
          owner.clear(lease);
          const receipt = `업로드한 사진 ${required(p.newItem)} · ${uploaded.affected_selections.length ? '현재 선택을 다시 확인해 주세요.' : '선택되지 않음'} · 확인 종료`;
          setMessage(receipt);
          onSaved(receipt);
          return;
        }
        // An originally unselected original stays unselected even if another actor selected it during upload.
        if (!p.assignment) {
          owner.clear(lease);
          const receipt = uploaded.affected_selections.length
            ? '업로드됨, 현재 선택을 다시 확인해 주세요.'
            : '업로드됨, 선택되지 않음';
          setMessage(receipt);
          onSaved(`업로드한 사진 ${required(p.newItem)} · ${receipt}`);
          return;
        }
        const originalSelection = c.affected_selections.find(
          (s) =>
            s.assignment_id === p.assignment &&
            s.displays.some((d) => d.collection_id === collectionId && d.item_id === itemId),
        );
        p.stage = originalSelection ? 'ready' : 'unapplied';
        setMessage(
          originalSelection
            ? '업로드됨, 적용 전'
            : '기존 사진은 현재 선택에 없습니다. 업로드한 사진을 적용하지 않고 확인을 종료할 수 있습니다.',
        );
        return;
      }
      if (p.stage === 'ready' || p.stage === 'unapplied') return;
      if (p.stage === 'item') {
        await requireActor();
        lease.sent = true;
        if (replaying) await owner.replayEdit(lease);
        else await replaceMediaItem(runtime.client, collectionId, itemId, required(p.itemBody));
        if (!current()) return;
        p.stage = 'verify';
      }
      if (p.stage === 'create-selection') {
        if (
          !replaying &&
          (!initialSelection ||
            ownerContext?.selection_state !== 'ABSENT' ||
            !p.createBody ||
            p.createBody.displays.some(
              (d) =>
                d.category !== initialSelection.category ||
                d.language !== initialSelection.language,
            ))
        )
          throw new Error('initial context');
        await requireActor();
        lease.sent = true;
        if (replaying) await owner.replayEdit(lease);
        else p.assignment = await createMediaSelection(runtime.client, required(p.createBody));
        if (!current()) return;
        p.stage = 'verify';
      }
      if (p.stage === 'selection') {
        await requireActor();
        lease.sent = true;
        if (replaying) await owner.replayEdit(lease);
        else
          await replaceMediaSelection(
            runtime.client,
            required(p.assignment),
            required(p.selectionBody),
          );
        if (!current()) return;
        p.stage = 'verify';
      }
      {
        owner.checking(lease);
        const c = await read(p.newItem ?? itemId);
        if (!current()) return;
        const verifiedSelection =
          p.selectionBody || p.createBody
            ? (await readOwnerMediaSelectionContext(runtime.client, c.owner)).current_selection
            : null;
        if (!current()) return;
        if (
          p.itemBody &&
          (c.primary_uri !== p.itemBody.primary_uri ||
            c.title !== p.itemBody.title ||
            c.preview_uri !== p.itemBody.preview_uri ||
            c.availability !== p.itemBody.availability ||
            c.expected_item_revision <= p.itemBody.expected_item_revision ||
            c.expected_collection_revision <= p.itemBody.expected_collection_revision ||
            c.affected_selections.length !== p.itemBody.affected_selections.length ||
            p.itemBody.affected_selections.some(
              (s) =>
                !c.affected_selections.some(
                  (a) =>
                    a.assignment_id === s.assignment_id &&
                    a.expected_assignment_revision > s.expected_assignment_revision &&
                    a.display_state === s.display_state &&
                    same(a.displays, s.displays),
                ),
            ))
        )
          throw new Error('effect');
        if (
          p.selectionBody &&
          (!verifiedSelection ||
            verifiedSelection.assignment_id !== p.assignment ||
            verifiedSelection.expected_assignment_revision <=
              p.selectionBody.expected_assignment_revision ||
            verifiedSelection.display_state !== p.selectionBody.display_state ||
            !same(verifiedSelection.displays, p.selectionBody.displays))
        )
          throw new Error('effect');
        if (
          p.createBody &&
          (!verifiedSelection ||
            verifiedSelection.assignment_id !== p.assignment ||
            verifiedSelection.display_state !== p.createBody.display_state ||
            !same(verifiedSelection.displays, p.createBody.displays))
        )
          throw new Error('effect');
        setContext(null);
        setSelection(null);
        owner.clear(lease);
        setMessage('변경 결과를 확인했습니다.');
        onSaved(
          p.newItem
            ? `변경 결과를 확인했습니다. 업로드한 사진 ${p.newItem}`
            : '변경 결과를 확인했습니다.',
        );
      }
    } catch (e) {
      if (!current()) {
        if (mounted()) {
          setError(true);
          setMessage('현재 수행자를 확인할 수 없습니다. 최신 문맥을 다시 조회해 주세요.');
          setContext(null);
        }
        return;
      }
      setError(true);
      if (isApiError(e) && e.status === 403) {
        owner.clear(lease);
        runtime.session.reportDomainAccessDenied('property', generation);
        setContext(null);
        setMessage('사진 편집 권한을 확인해 주세요.');
      } else if (isApiError(e) && e.status === 409) {
        if (p.stage !== 'verify') owner.rejectEdit(lease);
        p.conflict = true;
        setContext(null);
        setMessage('현재 선택이 바뀌었습니다. 최신 문맥을 조회하고 다시 확인해 주세요.');
        if (!p.newItem && p.stage !== 'verify') owner.clear(lease);
      } else if (isApiError(e) && e.status === 422) {
        owner.rejectEdit(lease);
        if (p.newItem) {
          // A definitive selection rejection must never discard an accepted upload.
          p.conflict = true;
          setContext(null);
        } else owner.clear(lease);
        setMessage(
          p.newItem
            ? '현재 선택 정보를 새로 조회한 뒤 적용 위치를 다시 확인해 주세요.'
            : '사진 입력 정보를 확인해 주세요.',
        );
      } else {
        if (p.stage === 'read') {
          owner.clear(lease);
          setContext(null);
        }
        setMessage(
          isApiError(e) && e.status === 404
            ? '현재 서버에서 사진 편집을 지원하지 않거나 대상이 없습니다.'
            : '결과를 확인하지 못했습니다. 같은 편집 요청을 다시 시도해 주세요.',
        );
      }
    } finally {
      owner.release(lease);
      if (mounted()) setBusy(false);
    }
  };
  const fresh = () => {
    if (mine?.stage === 'verify') {
      // The PUT was accepted: only current effect verification is safe now.
      void run(mine);
    } else if (mine?.newItem) {
      mine.conflict = false;
      mine.selectionBody = undefined;
      mine.stage = 'read-upload';
      void run(mine);
    } else void run({ edit: true, collectionId, itemId, stage: 'read' });
  };
  const dialogOpen = busy || !!context || !!mine || !!message || error;
  if (!canManageMedia(runtime, generation))
    return error && message ? (
      <p ref={focus} tabIndex={-1} role="alert">
        {message}
      </p>
    ) : null;
  return (
    <div className="grid min-w-0 gap-3 border-t pt-3" aria-busy={busy}>
      <Button variant="outline" disabled={blocked || !!mine} onClick={fresh}>
        사진 편집
      </Button>
      <Dialog
        open={dialogOpen}
        onOpenChange={(next) => {
          // 작업 중이거나 결과가 불명확한 요청이 남아 있으면 닫지 못하게 해 같은 요청 재시도 길을 유지한다.
          if (next || busy || mine) return;
          setContext(null);
          setSelection(null);
          setMessage('');
          setError(false);
        }}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>사진 편집</DialogTitle>
            <DialogDescription>
              사진 주소 교체와 홈페이지 노출 위치·순서를 확인하고 저장합니다.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="grid max-h-[70vh] min-w-0 gap-3 overflow-y-auto">
            {busy && <p role="status">{owner.progress || '현재 사진 문맥 확인 중…'}</p>}
            {message && !busy && (
              <p ref={focus} tabIndex={-1} role={error ? 'alert' : 'status'}>
                {message}
              </p>
            )}
            {context && contextEpoch === owner.epoch() && !blocked && !mine && (
              <>
                <p
                  ref={editHeading}
                  tabIndex={-1}
                  className="scroll-mt-[calc(var(--workspace-sticky-height,0px)+1rem)]"
                >
                  현재 선택 ·{' '}
                  {target.kind === 'PROPERTY'
                    ? '지점'
                    : target.kind === 'ROOM_TYPE'
                      ? '객실 유형'
                      : '슬롯'}
                </p>
                {context.codec_version === 2 &&
                context.item_replace_eligibility === 'SUPPORTED' &&
                context.availability !== null ? (
                  <>
                    <label className="grid gap-1">
                      교체할 사진 URL
                      <input
                        className="w-full min-w-0 rounded-md border p-2"
                        value={url}
                        onChange={(e) => {
                          setUrl(e.target.value);
                        }}
                        aria-invalid={!!url && !safeImageUri(url)}
                        aria-describedby={
                          !!url && !safeImageUri(url) ? `${fieldId}-url-error` : undefined
                        }
                      />
                    </label>
                    {!!url && !safeImageUri(url) && (
                      <p id={`${fieldId}-url-error`} role="alert">
                        HTTPS 사진 주소를 입력해 주세요.
                      </p>
                    )}
                    <Button
                      disabled={!safeImageUri(url)}
                      onClick={() =>
                        void run({
                          edit: true,
                          collectionId,
                          itemId,
                          stage: 'item',
                          itemBody: {
                            command_id: crypto.randomUUID(),
                            expected_owner_version: context.expected_owner_version,
                            expected_collection_revision: context.expected_collection_revision,
                            expected_item_revision: context.expected_item_revision,
                            title: context.title,
                            primary_uri: url,
                            preview_uri: context.preview_uri,
                            availability: required(context.availability),
                            affected_selections: [...context.affected_selections]
                              .sort((a, b) => a.assignment_id.localeCompare(b.assignment_id))
                              .map((s) => ({
                                ...selectionCommand(s),
                                assignment_id: s.assignment_id,
                              })),
                          },
                        })
                      }
                    >
                      URL 교체 저장
                    </Button>
                  </>
                ) : context.codec_version === 2 ? (
                  <p>
                    {context.availability === null
                      ? '현재 사진의 사용 상태를 확인할 수 없어 URL 교체를 지원하지 않습니다.'
                      : '참조 키가 일치하지 않아 URL 교체를 지원하지 않습니다.'}
                  </p>
                ) : (
                  <>
                    <label className="grid min-w-0 gap-1">
                      교체할 사진 파일
                      <input
                        type="file"
                        aria-invalid={!!file && !validFile(file)}
                        aria-describedby={
                          file && !validFile(file) ? `${fieldId}-file-error` : undefined
                        }
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(e) => {
                          setFile(e.target.files?.[0] ?? null);
                        }}
                      />
                    </label>
                    {file && !validFile(file) && (
                      <p id={`${fieldId}-file-error`} role="alert">
                        10 MB 이하의 JPG, PNG, WebP 파일을 선택해 주세요.
                      </p>
                    )}
                    <Button
                      disabled={!validFile(file)}
                      onClick={() =>
                        file &&
                        void run({
                          edit: true,
                          collectionId,
                          itemId,
                          stage: 'upload',
                          assignment: context.affected_selections[0]?.assignment_id,
                          context,
                          upload: {
                            commandId: crypto.randomUUID(),
                            collectionId,
                            expectedCollectionRevision: context.expected_collection_revision,
                            file,
                          },
                        })
                      }
                    >
                      파일 교체 시작
                    </Button>
                  </>
                )}
                {selection &&
                (initialSelection?.selectionSupported !== false ||
                  selection.displays.some(
                    (d) => d.collection_id === collectionId && d.item_id === itemId,
                  )) ? (
                  <fieldset className="grid min-w-0 gap-2 rounded-md border p-2">
                    <legend>저장된 선택 순서·표시</legend>
                    <p className="body-sm text-muted-foreground">
                      전체 선택을 저장합니다. 웹사이트 사용을 켜면 저장 즉시 공개 선택에 적용됩니다.
                      실제 노출은 공개 조회에서 확인하세요.
                    </p>
                    {!selection.displays.some(
                      (d) => d.collection_id === collectionId && d.item_id === itemId,
                    ) &&
                      initialSelection && (
                        <>
                          {target.kind === 'PROPERTY' && (
                            <Choice
                              label="추가할 사용 위치"
                              value={newRole}
                              disabled={blocked}
                              options={{
                                PROPERTY_HERO: '지점 대표 사진',
                                PROPERTY_CARD: '지점 목록 사진',
                              }}
                              onChange={(value) => {
                                setNewRole(value as typeof newRole);
                              }}
                            />
                          )}
                          <Button
                            disabled={!['ko', 'en', 'und'].includes(initialSelection.language)}
                            variant="outline"
                            onClick={() => {
                              const role =
                                target.kind === 'PROPERTY'
                                  ? newRole
                                  : target.kind === 'ROOM_TYPE'
                                    ? 'ROOM_TYPE_GALLERY'
                                    : 'UNIT_FLOOR_PLAN';
                              const compatible = selection.displays.filter((d) => d.role === role);
                              const groupOrder =
                                compatible.find((d) => d.collection_id === collectionId)
                                  ?.group_order ??
                                Math.max(-1, ...compatible.map((d) => d.group_order)) + 1;
                              const imageOrder =
                                Math.max(
                                  -1,
                                  ...compatible
                                    .filter((d) => d.group_order === groupOrder)
                                    .map((d) => d.image_order),
                                ) + 1;
                              setSelection({
                                ...selection,
                                displays: [
                                  ...selection.displays,
                                  {
                                    collection_id: collectionId,
                                    item_id: itemId,
                                    role,
                                    category: initialSelection.category,
                                    language: initialSelection.language as 'ko' | 'en' | 'und',
                                    group_order: groupOrder,
                                    image_order: imageOrder,
                                  },
                                ],
                              });
                            }}
                          >
                            이 사진을 선택 목록에 추가
                          </Button>
                        </>
                      )}

                    <label>
                      <input
                        type="checkbox"
                        checked={selection.display_state === 'VISIBLE'}
                        onChange={(e) => {
                          setSelection({
                            ...selection,
                            display_state: e.target.checked ? 'VISIBLE' : 'HIDDEN',
                          });
                        }}
                      />
                      웹사이트에서 사용
                    </label>
                    <ol className="grid gap-2">
                      {selection.displays.map((d, i) => (
                        <li
                          key={rowId(d)}
                          ref={(node) => {
                            if (node) selectionRows.current.set(rowId(d), node);
                            else selectionRows.current.delete(rowId(d));
                          }}
                          className="flex min-w-0 flex-wrap items-center gap-2 rounded border p-2"
                        >
                          <SelectionPhoto
                            {...registeredPhoto(ownerContext, d.collection_id, d.item_id)}
                            collectionId={d.collection_id}
                            itemId={d.item_id}
                          />
                          <span>
                            선택 {i + 1} ·{' '}
                            {d.role === 'UNIT_FLOOR_PLAN'
                              ? '슬롯 평면도'
                              : SELECTION_ROLE_LABEL[d.role]}
                            {d.collection_id === collectionId && d.item_id === itemId
                              ? ' · 현재 사진'
                              : ''}
                          </span>
                          {([-1, 1] as const).map((step) => {
                            const neighbor = selection.displays[i + step];
                            return (
                              <Button
                                key={step}
                                variant="outline"
                                data-step={step}
                                disabled={
                                  neighbor?.role !== d.role ||
                                  neighbor.language !== d.language ||
                                  neighbor.category !== d.category ||
                                  neighbor.group_order !== d.group_order
                                }
                                aria-label={`선택 ${String(i + 1)} ${step < 0 ? '위로' : '아래로'}`}
                                onClick={(event) => {
                                  const displays = [...selection.displays];
                                  const other = required(displays[i + step]);
                                  const moving = required(displays[i]);
                                  displays[i + step] = {
                                    ...moving,
                                    image_order: other.image_order,
                                  };
                                  displays[i] = { ...other, image_order: moving.image_order };
                                  rowIds.set(required(displays[i + step]), rowId(moving));
                                  rowIds.set(required(displays[i]), rowId(other));
                                  if (document.activeElement === event.currentTarget)
                                    movedFocus.current = { id: rowId(moving), step };
                                  setSelection({ ...selection, displays });
                                }}
                              >
                                {step < 0 ? '위로' : '아래로'}
                              </Button>
                            );
                          })}
                          <Button
                            variant="outline"
                            aria-label={`선택 ${String(i + 1)} 제외`}
                            onClick={(event) => {
                              const removingFocusedRow = event.currentTarget
                                .closest('li')
                                ?.contains(document.activeElement);
                              setSelection({
                                ...selection,
                                displays: selection.displays.filter(
                                  (_display, index) => index !== i,
                                ),
                              });
                              if (removingFocusedRow)
                                editHeading.current?.focus({ preventScroll: true });
                            }}
                          >
                            선택에서 제외
                          </Button>
                        </li>
                      ))}
                    </ol>
                    <p className="caption text-muted-foreground">
                      선택에서 제외해도 사진 원본은 보관됩니다. 선택 저장 후 적용됩니다.
                    </p>
                    <Button
                      onClick={() =>
                        void run({
                          edit: true,
                          collectionId,
                          itemId,
                          stage: 'selection',
                          assignment: selection.assignment_id,
                          selectionBody: {
                            ...selectionCommand(selection),
                            expected_owner_version: required(ownerContext).expected_owner_version,
                          },
                        })
                      }
                    >
                      선택 저장
                    </Button>
                  </fieldset>
                ) : initialSelection?.selectionSupported === false ? (
                  <p>
                    공용공간 사진은 보관할 수 있습니다. 이 위치의 웹사이트 사용 설정은 아직 지원하지
                    않습니다.
                  </p>
                ) : initialSelection && ownerContext?.selection_state === 'ABSENT' ? (
                  <fieldset className="grid gap-3 rounded-md border p-3">
                    <legend>사용할 사진 선택</legend>
                    <p>저장된 선택 없음 · 선택되지 않음</p>
                    {target.kind === 'PROPERTY' && (
                      <label className="grid gap-1">
                        사용 위치
                        <Choice
                          label="사용 위치"
                          value={newRole}
                          disabled={blocked}
                          options={{
                            PROPERTY_HERO: '지점 대표 사진',
                            PROPERTY_CARD: '지점 목록 사진',
                          }}
                          onChange={(value) => {
                            setNewRole(value as typeof newRole);
                          }}
                        />
                      </label>
                    )}
                    <label>
                      <input
                        type="checkbox"
                        checked={newVisible}
                        onChange={(event) => {
                          setNewVisible(event.target.checked);
                        }}
                      />{' '}
                      고객 공개에 사용할 사진으로 선택
                    </label>
                    <p className="body-sm text-muted-foreground">
                      이 사진 한 장의 새 선택을 저장합니다. 기존 선택은 교체하지 않습니다. 실제 고객
                      결과는 같은 기간·언어로 별도 조회하세요.
                    </p>
                    <Button
                      onClick={() =>
                        void run({
                          edit: true,
                          collectionId,
                          itemId,
                          stage: 'create-selection',
                          createBody: {
                            expected_owner_version: required(ownerContext).expected_owner_version,
                            command_id: crypto.randomUUID(),
                            scope_kind: context.owner
                              .scope_kind as CreateMediaSelection['scope_kind'],
                            property_id: context.owner.property_id,
                            room_type_id: context.owner.room_type_id,
                            room_id: context.owner.room_id,
                            unit_id: context.owner.unit_id,
                            display_state: newVisible ? 'VISIBLE' : 'HIDDEN',
                            displays: [
                              {
                                collection_id: collectionId,
                                item_id: itemId,
                                role:
                                  target.kind === 'PROPERTY'
                                    ? newRole
                                    : target.kind === 'ROOM_TYPE'
                                      ? 'ROOM_TYPE_GALLERY'
                                      : 'UNIT_FLOOR_PLAN',
                                category: initialSelection.category,
                                language:
                                  initialSelection.language as CreateMediaSelection['displays'][number]['language'],
                                group_order: 0,
                                image_order: 0,
                              },
                            ],
                          },
                        })
                      }
                    >
                      {newVisible ? '고객 공개 선택 확인·저장' : '이 사진 선택 저장'}
                    </Button>
                  </fieldset>
                ) : (
                  <p>
                    {ownerContext?.selection_state === 'RESERVED'
                      ? '삭제 예약된 선택이 있어 새 선택을 만들 수 없습니다. '
                      : ''}
                    이 사진은 선택되지 않았습니다. 기존 전체 선택의 현재값이 필요합니다. 다른 선택
                    사진의 편집에서 현재 선택을 확인하세요.
                  </p>
                )}
              </>
            )}
            {mine && !busy && !owner.progress && (
              <>
                {mine.newItem && (
                  <section aria-label="사진 교체 확인" className="grid min-w-0 gap-3">
                    <figure className="grid min-w-0 gap-1">
                      <figcaption>원본 사진</figcaption>
                      {originalPreview ?? <p>원본 미리보기를 확인해 주세요.</p>}
                      <p className="caption break-all">원본 ID: {itemId}</p>
                    </figure>
                    <figure className="grid min-w-0 gap-1">
                      <figcaption>새 사진</figcaption>
                      {localPreview?.file === previewFile && (
                        <img
                          src={localPreview.uri}
                          alt="새 사진 미리보기"
                          className="aspect-video w-full rounded-md object-contain"
                        />
                      )}
                      <p className="break-all">{mine.upload?.file.name}</p>
                      <p className="caption break-all">업로드한 사진 ID: {mine.newItem}</p>
                    </figure>
                    {!mine.conflict && (mine.stage === 'ready' || mine.stage === 'unapplied') && (
                      <div>
                        <p>최신 문맥의 교체 대상 위치</p>
                        <ul aria-label="현재 교체 대상 위치">
                          {mine.context?.affected_selections.flatMap((s) =>
                            s.displays
                              .filter(
                                (d) => d.collection_id === collectionId && d.item_id === itemId,
                              )
                              .map((d, i) => (
                                <li key={`${s.assignment_id}-${String(i)}`}>
                                  {d.role === 'UNIT_FLOOR_PLAN'
                                    ? '슬롯 평면도'
                                    : SELECTION_ROLE_LABEL[d.role]}{' '}
                                  · {d.group_order}-{d.image_order}
                                </li>
                              )),
                          )}
                        </ul>
                        {mine.stage === 'unapplied' && <p>현재 교체할 선택 위치가 없습니다.</p>}
                      </div>
                    )}
                  </section>
                )}
                {mine.newItem && message !== '업로드됨, 적용 전' && (
                  <p role="status">업로드됨, 적용 전</p>
                )}
                {mine.stage === 'ready' && !mine.conflict && (
                  <Button
                    onClick={() => {
                      const s = mine.context?.affected_selections.find(
                        (s) => s.assignment_id === mine.assignment,
                      );
                      if (!s) {
                        setMessage(
                          '현재 선택이 바뀌었습니다. 최신 문맥을 조회하고 다시 확인해 주세요.',
                        );
                        mine.conflict = true;
                        return;
                      }
                      mine.selectionBody = {
                        ...selectionCommand(s),
                        expected_owner_version: required(mine.context).expected_owner_version,
                        displays: s.displays.map((d) =>
                          d.collection_id === collectionId && d.item_id === itemId
                            ? { ...d, item_id: required(mine.newItem) }
                            : { ...d },
                        ),
                      };
                      mine.stage = 'selection';
                      void run(mine);
                    }}
                  >
                    업로드한 사진 적용
                  </Button>
                )}
                {mine.stage === 'unapplied' && !mine.conflict && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      mine.stage = 'acknowledge';
                      void run(mine);
                    }}
                  >
                    적용하지 않고 확인 종료
                  </Button>
                )}
                {mine.conflict ? (
                  <Button variant="outline" onClick={fresh}>
                    최신 문맥 다시 조회
                  </Button>
                ) : (
                  mine.stage !== 'ready' &&
                  mine.stage !== 'unapplied' && (
                    <Button variant="outline" onClick={() => void run(mine)}>
                      같은 편집 요청 재시도
                    </Button>
                  )
                )}
              </>
            )}
            {!mine && error && (
              <Button variant="outline" disabled={blocked} onClick={fresh}>
                최신 문맥 다시 조회
              </Button>
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </div>
  );
}
